// Central FAQ content for Chleb AI.
// Every answer here is sourced from copy that already exists on the site
// (Pricing, Crm, AiReceptionist, WhyUs, About, Verifications, Privacy Policy,
// Terms of Service, and the seven industry vertical pages in verticals.ts).
// Where the site does not state something, the answer says so plainly
// instead of inventing a number or policy, see `gap` and `gapNote`.
//
// `pages` controls where each question is eligible to appear (home teaser,
// a specific product page, or the central /faq page only). The central FAQ
// page renders every publishable item. Product pages render a curated subset
// relevant to the decision being made on that page.

export type FaqCategory =
  | "how-it-works"
  | "setup-timeline"
  | "pricing-contracts"
  | "quality-control"
  | "integrations"
  | "data-security"
  | "support";

export const FAQ_CATEGORIES: { key: FaqCategory; label: string }[] = [
  { key: "how-it-works", label: "How it works" },
  { key: "setup-timeline", label: "Setup & timeline" },
  { key: "pricing-contracts", label: "Pricing & contracts" },
  { key: "quality-control", label: "Quality & control" },
  { key: "integrations", label: "Integrations" },
  { key: "data-security", label: "Data & security" },
  { key: "support", label: "Support" },
];

export type FaqPage =
  | "home"
  | "pricing"
  | "crm"
  | "ai-receptionist"
  | "why-us"
  | "missed-call-recovery"
  | "tampa"
  | "tampa-web-design";

export interface FaqItem {
  id: string;
  category: FaqCategory;
  question: string;
  answer: string;
  /** Pages (besides the central /faq page) this question should also appear on. */
  pages: FaqPage[];
  /** True if the site does not actually publish the information needed to answer this fully. */
  gap?: boolean;
  /** Explains exactly what's missing, for the owner to fill in. */
  gapNote?: string;
}

export const FAQS: FaqItem[] = [
  // ── How it works ──────────────────────────────────────────────
  {
    id: "what-is-chleb",
    category: "how-it-works",
    question: "What exactly is Chleb AI?",
    answer:
      "Chleb AI is revenue infrastructure for service businesses: an AI receptionist and website chatbot that answer and qualify every call and message, plus a branded CRM that turns each of those interactions into a tracked contact, a booked appointment, and a follow-up sequence. You can buy the CRM alone, the AI receptionist and chatbot alone, or both together as the Front Office Bundle. Custom websites and SEO sit on top of any plan and are scoped separately.",
    pages: ["home"],
  },
  {
    // 2026-10-03 (revenue goal): a Google AI Overview in the 2026-09-27 daily
    // SEO sample (tampa-number-one/raw/2026-09-27T1045ET_daily_samples.md)
    // named the company "Kleb AI" and quoted "$2.97 to $9.97/month"; automatic
    // video captions are the likely source (inferred). One plain answer with
    // the right spelling and the published prices (the "what-do-i-pay"
    // figures and sentence) gives answer engines a first-party source to
    // match. Central /faq only.
    id: "name-spelling",
    category: "how-it-works",
    question: "Is it Chleb AI or Kleb AI?",
    answer:
      "It is Chleb AI, spelled C-H-L-E-B, at chleb.ai, run from Tampa, Florida. Kleb AI is a misspelling of Chleb AI. The published monthly prices are $297 for the CRM, $899 for the AI Receptionist + Chatbot and $997 for the Front Office Bundle. Call minutes and emails are billed separately at carrier cost, with no Chleb markup.",
    pages: [],
  },
  {
    id: "how-does-ai-answer-calls",
    category: "how-it-works",
    question: "How does the AI receptionist actually handle a call?",
    answer:
      "A speech-to-text model transcribes what the caller says, a large language model decides what to say next based on your configured call flow and business rules, and a text-to-speech model speaks the reply, fast enough that the exchange feels like a normal phone conversation. The agent is trained on your services, hours, and booking calendar during onboarding, not on a generic script.",
    pages: ["ai-receptionist", "tampa"],
  },
  {
    id: "real-calendar-booking",
    category: "how-it-works",
    question: "Does the AI actually book appointments on my real calendar, or just take a message?",
    answer:
      "The receptionist can book into a supported connected calendar. Available slots depend on the calendar connection, conflict calendars, hours, notice periods and appointment rules configured for your account. Before launch, test a busy slot, simultaneous requests and a disconnected calendar. If a booking cannot be confirmed, the call flow should collect the request for your team rather than promise an appointment.",
    pages: ["ai-receptionist", "crm"],
  },
  {
    id: "handoff-to-human",
    category: "how-it-works",
    question: "What happens when a caller needs a real person?",
    answer:
      "During setup, agree which calls need a person, who receives them and what happens if nobody answers. The configured handoff can include the caller's details and available call summary or transcript. Test an urgent call, a failed transfer and an out-of-hours request before launch; a routing rule alone does not prove someone will receive the call.",
    pages: ["ai-receptionist", "why-us", "tampa"],
  },
  {
    id: "hear-it-first",
    category: "how-it-works",
    question: "Can I hear what it actually sounds like before I buy?",
    answer:
      "Yes. Call (813) 694-8671 right now and you're talking to the same AI your customers would get, or try the live demo on the homepage. Book a discovery call and we'll run a live sample call in your industry, with your business name and pricing built in, before you decide anything.",
    pages: ["home", "crm", "why-us"],
  },
  {
    id: "24-7-calendar-booking",
    category: "how-it-works",
    question: "Does it answer 24/7 and book directly into my calendar, or just take a message?",
    answer:
      "The AI Receptionist plan provides around-the-clock answering and can book into a supported connected calendar. Confirm the account's availability rules and booking behavior during setup. Some jobs need staff approval; those should be recorded as requests awaiting confirmation, not confirmed appointments.",
    pages: ["home", "ai-receptionist"],
  },
  {
    id: "missed-call-text-back",
    category: "how-it-works",
    question: "What is missed-call text-back, and does Chleb do it?",
    answer:
      "Missed-call text-back sends an automated SMS after an unanswered business call. Chleb can acknowledge the call and offer a booking step or callback. Confirm the sending number, messaging setup, consent and opt-out handling before launch. Call forwarding alone does not establish that texts can be sent from the number the customer dialed; carrier delays and delivery failures can occur.",
    pages: ["home", "missed-call-recovery"],
  },
  {
    id: "missed-call-next-step",
    category: "how-it-works",
    question: "What can the automatic missed-call text help the caller do?",
    answer:
      "The message can acknowledge the missed call, offer a booking link or callback window, and let the caller continue by text. The exact next step is configured around how your team schedules and handles urgent requests.",
    pages: ["missed-call-recovery"],
  },
  {
    id: "receptionist-and-recovery",
    category: "how-it-works",
    question: "Does missed-call recovery replace the AI receptionist?",
    answer:
      "No. The AI receptionist handles inbound calls when it can answer them. Missed-call recovery is the backup path for an unanswered or dropped call, so the caller still receives a fast next step instead of reaching a dead end.",
    pages: ["missed-call-recovery"],
  },
  {
    id: "answering-service-difference",
    category: "how-it-works",
    question: "How is Chleb different from a traditional answering service?",
    answer:
      "A traditional answering service often takes a message or routes the call. Chleb is configured around your services, intake rules, and calendar so it can qualify the caller, book an available time, and record the outcome in the CRM. The discovery call confirms whether that workflow fits your business before launch.",
    pages: ["home", "ai-receptionist"],
  },
  {
    id: "smith-ai-goodcall-alternative",
    category: "how-it-works",
    question: "Is there a Smith.ai or Goodcall alternative built for local service businesses?",
    // Product scope checked 2026-09-09 against https://smith.ai/ and
    // https://www.goodcall.com/pricing. Full dated sources are shown on the
    // corresponding comparison pages. Avoid competitor price snapshots here.
    answer:
      "Chleb offers an AI Receptionist + Chatbot plan for service businesses, a separate CRM, and a Front Office Bundle combining both. Smith.ai offers AI-first, hybrid, and human-first receptionist services. Goodcall offers configurable AI phone agents with self-service plans and an enterprise option. Compare the answering model, setup responsibilities, handoff, and complete cost in the dated comparison pages. Chleb publishes its subscription prices; call minutes and emails are separate carrier-cost charges.",
    pages: ["home"],
  },
  {
    id: "tampa-location",
    category: "how-it-works",
    question: "Is Chleb AI based in Tampa, Florida?",
    answer:
      "Yes. Chleb Holdings LLC, doing business as Chleb AI, is based in Tampa, Florida and serves appointment-driven businesses across the United States.",
    pages: ["home", "tampa"],
  },

  // ── Setup & timeline ──────────────────────────────────────────
  {
    id: "how-fast-live",
    category: "setup-timeline",
    question: "How fast can I actually go live?",
    answer:
      "The team builds your CRM account after setup and payment terms are agreed. Receptionist and bundle onboarding includes your business information, configuration, and test calls before your number goes live. Confirm the launch schedule with the team around the access and setup your business needs.",
    pages: ["crm", "pricing", "tampa"],

  },
  {
    id: "discovery-call-covers",
    category: "setup-timeline",
    question: "What actually happens on the discovery call?",
    answer:
      "We review your current bottleneck, relevant services, and integration requirements. For websites and ongoing care, we check access and delivery capacity, then prepare a written proposal covering recurring tasks, limits, one-time work, third-party costs, and timing. Booking discovery does not authorize work or start a subscription.",
    pages: ["pricing"],
  },
  {
    id: "existing-tools-required",
    category: "setup-timeline",
    question: "Do I need to already have a calendar, CRM, or phone system in place?",
    answer:
      "The discovery call includes an integration check to verify whether your existing scheduling tool, CRM, and phone provider are supported.",
    pages: [],
    gap: true,
    gapNote:
      "The site never states what happens if you don't have any of these yet, whether Chleb provisions a phone number and calendar from scratch, and at what cost. Worth a plain answer for solo operators and new businesses.",
  },
  {
    id: "who-builds-script",
    category: "setup-timeline",
    question: "Who writes and tunes my AI's call flow and script?",
    answer:
      "We do, hands-on. The founders build the call flow, write the prompts, run live test calls, and refine until the agent sounds like your best employee on their best day, using your actual services and pricing, not a generic template.",
    pages: ["why-us"],
  },
  {
    id: "setup-fee-question",
    category: "setup-timeline",
    question: "Is there a setup fee on top of the monthly price?",
    answer:
      "Website builds and integration setup may involve a one-time fee. A written proposal separates those projects from monthly care or SEO, annual domain renewals, and other third-party costs before work begins.",
    pages: [],
    gap: true,
    gapNote:
      "The site never states whether the $297 CRM, $899 AI Receptionist, or $997 bundle plans carry a setup fee in addition to the published monthly price. The pricing tables only show the recurring number. This should be confirmed and stated explicitly (e.g. \"no setup fee on published plans\") to avoid a surprise at checkout.",
  },

  // ── Pricing & contracts ───────────────────────────────────────
  {
    id: "what-do-i-pay",
    category: "pricing-contracts",
    question: "What exactly do I pay each month?",
    answer:
      "The published monthly subscriptions are $297 for CRM, $899 for AI Receptionist + Chatbot, and $997 for the Front Office Bundle combining both. Ask about any plan to confirm availability and setup. Call minutes and emails are billed separately at carrier cost, with no Chleb markup. Review the billing interval and terms before purchasing.",
    pages: ["pricing", "crm", "home"],
  },
  {
    id: "usage-costs",
    category: "pricing-contracts",
    question: "What about usage, such as call minutes, texts, and emails? Are there overage fees?",
    answer:
      "Call minutes and emails are billed separately at carrier cost to the card in your account, with no Chleb markup. Usage and subscription are separate charges, so your total can vary with usage. Confirm any other usage or custom-work charges in the scope before purchasing.",
    pages: ["pricing", "crm"],
  },
  {
    id: "contract-length",
    category: "pricing-contracts",
    question: "How does cancelling work?",
    answer:
      "Monthly plans renew each month and stop at the next renewal when you cancel. Annual plans run for the full year. Payments already made are not refunded.",
    pages: ["pricing", "crm", "why-us"],
  },
  {
    id: "website-seo-pricing",
    category: "pricing-contracts",
    question: "How is the website or SEO service priced, since there's no sticker price?",
    answer:
      "Custom multi-page websites and ongoing SEO are scoped to your business. A one-page site and a twenty-page build with an ongoing SEO program aren't the same project, so a single public number would either overcharge one or undercharge the other. You get a specific monthly and setup cost plus a specific go-live date on the discovery call instead.",
    pages: ["pricing", "crm", "tampa-web-design"],
  },
  {
    id: "refunds",
    category: "pricing-contracts",
    question: "Are there refunds if I cancel?",
    answer:
      "Monthly plans renew each month and stop at the next renewal when you cancel. Annual plans run for the full year. Payments already made are not refunded.",
    pages: ["pricing"],
  },
  {
    id: "vs-answering-service-vs-human",
    category: "pricing-contracts",
    question: "AI receptionist vs. an answering service vs. hiring a receptionist, which is cheaper?",
    // Compare cost categories without invented staffing estimates or stale vendor prices.
    answer:
      "There is no universal cheapest option because the scope differs. Compare employee wages, staffing coverage, and management time with an answering service's plan, usage, and add-ons. Chleb's AI Receptionist + Chatbot is $899 monthly; the Front Office Bundle including CRM is $997 monthly. Call minutes and emails are billed separately at carrier cost. Compare the same intake, handoff, and follow-up requirements before choosing.",
    pages: ["home", "ai-receptionist"],
  },

  // ── Quality & control ─────────────────────────────────────────
  {
    id: "sound-robotic",
    category: "quality-control",
    question: "Will it sound robotic on the phone?",
    answer:
      "We run live test calls in your industry before launch and tune the script until it stops sounding scripted. The receptionist uses natural pacing, and when a caller asks whether it is an AI, it says so right away.",
    pages: ["why-us", "ai-receptionist"],
  },
  {
    id: "ai-mistake",
    category: "quality-control",
    question: "What happens if the AI mishandles a call?",
    answer:
      "Flag the call and the response that needs correction so the team can review the configured flow and retest that scenario. Recordings and transcripts are available when enabled and permitted by the account's recording and consent settings. Agree how callers reach a person and how unresolved requests are followed up; do not rely on the AI to resolve every exception.",
    pages: ["why-us"],
  },
  {
    id: "review-calls",
    category: "quality-control",
    question: "Can I review calls and see what the AI is actually saying to my customers?",
    answer:
      "The AI Receptionist plan includes recordings and transcripts when enabled and permitted by your recording and consent settings. With the Front Office Bundle, configured call and booking activity can connect to the CRM record. Confirm which records appear, their retention and how you can export them during setup. A recorded call or appointment is not proof of collected revenue; revenue reporting requires your team to maintain accurate outcomes.",
    pages: ["ai-receptionist", "crm"],
  },
  {
    id: "knows-my-industry",
    category: "quality-control",
    question: "Does it actually understand my industry's terms and pricing, or is it generic?",
    answer:
      "It's built around your business specifically: your services, your hours, your pricing, and your booking calendar, plus industry-specific vocabulary (what a \"ceramic coating\" means in detailing, what \"split AC service\" means in HVAC) configured during onboarding. There are dedicated pages per vertical because the call flow differs by industry.",
    pages: ["ai-receptionist"],
  },
  {
    id: "niche-business",
    category: "quality-control",
    question: "What if my business is niche or doesn't fit a standard call flow?",
    answer:
      "We build the prompt and call flow around your actual services and pricing during onboarding, not a generic template. If your offer genuinely doesn't fit a workable flow, we tell you before you sign, not after. Chleb may not fit complex multi-step legal-style intake or markets needing language coverage we do not yet serve. Our managed receptionist qualification guidance is roughly 30 calls a week or more. This is Chleb-specific guidance, not a minimum for every AI receptionist. Review missed-call demand, staff workload, handoff and total cost; lower-volume businesses can compare simpler follow-up or other answering options.",
    pages: ["why-us", "ai-receptionist"],
  },

  // ── Integrations ──────────────────────────────────────────────
  {
    id: "calendar-integration",
    category: "integrations",
    question: "Does it work with my existing calendar or scheduling tool?",
    answer:
      "The discovery call includes an integration check specifically to verify whether your scheduling tool, CRM, and phone provider are supported before you commit.",
    pages: [],
    gap: true,
    gapNote:
      "No page lists which specific calendar tools, CRMs, or phone and carrier providers are actually supported. Buyers researching before a call have no way to self-qualify on this. A published integrations list (or at minimum \"most major calendar and phone providers\") would close this gap.",
  },
  {
    id: "existing-crm",
    category: "integrations",
    question: "Can I use the AI receptionist if I already run a different CRM?",
    answer:
      "Not stated on the site.",
    pages: [],
    gap: true,
    gapNote:
      "The receptionist and chatbot plans clearly write leads into the Chleb CRM. Whether the receptionist can instead push data into a customer's existing CRM (or only into Chleb's) is never addressed. This matters to any buyer who has already invested in a CRM and doesn't want two systems of record.",
  },
  {
    id: "chatbot-existing-site",
    category: "integrations",
    question: "Does the website chatbot work on my current website, or do I need a new one?",
    answer:
      "The CRM plan includes one landing page built by the Chleb team, and the chatbot ships as part of the AI Receptionist + Chatbot plan.",
    pages: ["tampa-web-design"],
    gap: true,
    gapNote:
      "It's not explicit whether the chatbot can be embedded on a customer's existing website (a script tag, for example) or whether it only ships on the landing page Chleb builds. Worth a direct one-line answer either way.",
  },
  {
    id: "website-chat-and-booking",
    category: "integrations",
    question: "Can I get a website with AI chat and online booking, not just the phone system?",
    answer:
      "Yes. The CRM plan includes a landing page with online booking, and the AI Receptionist plan includes a website chatbot that can qualify visitors and book appointments. Custom multi-page websites and SEO are scoped on the discovery call.",
    pages: ["pricing", "crm", "tampa-web-design"],
  },
  {
    id: "switch-plans",
    category: "integrations",
    question: "Can I start with one plan and add the other later?",
    answer:
      "Yes. Tell us and we switch your plan; the bundle price applies from your next billing cycle.",
    pages: ["crm", "pricing", "tampa"],
  },
  {
    id: "crm-gohighlevel",
    category: "integrations",
    question: "Is the Chleb CRM built on GoHighLevel, and does it include calendar booking?",
    answer:
      "Yes. The Chleb CRM is a branded workspace built on Go High Level, giving you pipelines and deal stages, an appointment booking calendar, email follow-up sequences, tagging, review automation, and reporting, plus one landing page built by our team, for $297 monthly. In the Front Office Bundle, call and booking activity from the AI receptionist writes into that same calendar and pipeline automatically; bought on its own, the CRM keeps its own calendar and pipeline.",
    pages: ["crm", "pricing"],
  },

  // ── Data & security ───────────────────────────────────────────
  {
    id: "data-ownership",
    category: "data-security",
    question: "Who owns my call recordings, transcripts, and customer data?",
    answer:
      "Your customer records, call recordings and transcripts belong to you, as described in our Privacy policy. Chleb does not sell your data. Confirm the available export formats, timing, any fees, retention, deletion and access after cancellation in your agreement before launch. These customer records are distinct from Chleb's proprietary tools and templates; this answer does not promise instant export of every record under every configuration.",
    pages: ["why-us"],
  },
  {
    id: "hipaa-gdpr",
    category: "data-security",
    question: "Is Chleb AI HIPAA and GDPR compliant?",
    answer:
      "Chleb holds no third-party security certification: we are not SOC 2 audited or ISO 27001 certified. Chleb does not sign Business Associate Agreements today and is not set up to handle protected health information; a healthcare practice should ask about its setup on a call. A signed agreement does not create a certification, and platform safeguards alone do not make an implementation HIPAA compliant. Review the actual data flow, permitted use, access, retention and applicable obligations before sending sensitive information through the service. Our Security and Privacy pages describe the currently published safeguards and limitations.",
    pages: [],
  },
  {
    id: "sell-data",
    category: "data-security",
    question: "Do you sell or share my data with anyone?",
    answer:
      "No. We do not sell personal information to advertisers or data brokers, and mobile information is never shared with third parties for marketing or promotional purposes. We do share data with the vendors required to run the service itself, including hosting and infrastructure providers, the CRM and analytics platforms, communication providers, and payment processors like Stripe, under contractual safeguards, and with government authorities when legally required.",
    pages: [],
  },

  // ── Support ───────────────────────────────────────────────────
  {
    id: "post-launch-support",
    category: "support",
    question: "What support do I get after launch?",
    answer:
      "Front Office Bundle customers get priority onboarding (built and launched first) and a dedicated account contact instead of a ticket queue.",
    pages: [],
    gap: true,
    gapNote:
      "Dedicated support is only explicitly described for the Front Office Bundle. The site never states the support channel, response time, or hours for customers on the standalone CRM ($297) or standalone AI Receptionist ($899) plans. Buyers comparing tiers will want to know what they're giving up by not bundling.",
  },
  {
    id: "customer-reference",
    category: "support",
    question: "Can I talk to a current customer before I sign up?",
    answer:
      "Chleb AI does not currently publish a customer reference program.",
    pages: [],
    gap: true,
    gapNote:
      "No named, verifiable case studies or reference-call process are published yet. Add this only after a customer has explicitly approved being a reference.",
  },
  {
    id: "downtime-sla",
    category: "support",
    question: "What happens if the phone number or system goes down?",
    answer:
      "Not stated on the site.",
    pages: [],
    gap: true,
    gapNote:
      "There is no published uptime commitment, incident-response process, or SLA anywhere on the site. For a product whose entire pitch is \"never miss a call,\" buyers will ask this, and right now there's no answer to give them beyond a verbal one on the discovery call.",
  },
];

export const getFaqsForPage = (page: FaqPage) => FAQS.filter((f) => f.pages.includes(page));
export const getFaqsByCategory = (category: FaqCategory) =>
  FAQS.filter((f) => f.category === category && !f.gap);
